import { cn } from "@/utils/cn"
import { useInView } from "react-intersection-observer"
import { motion } from "motion/react"
import { useMemo, useEffect, useState } from "react"

interface GridBgProps {
  className?: string
}

export function GridBg({ className }: GridBgProps) {
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  })

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const updateDimensions = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }
    updateDimensions()
    window.addEventListener("resize", updateDimensions)
    return () => window.removeEventListener("resize", updateDimensions)
  }, [])

  return (
    <svg
      ref={ref}
      className={cn(
        "absolute inset-0 z-0 h-full w-full text-viola-500",
        className
      )}
      stroke="currentColor"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
    >
      {dimensions.width <= 390 ? (
        <AnimatedGrid
          gridSize={130}
          startAnimation={inView}
          viewportWidth={dimensions.width}
          viewportHeight={dimensions.height}
        />
      ) : dimensions.width <= 960 ? (
        <AnimatedGrid
          gridSize={192}
          startAnimation={inView}
          viewportWidth={dimensions.width}
          viewportHeight={dimensions.height}
        />
      ) : dimensions.width <= 1440 ? (
        <AnimatedGrid
          gridSize={288}
          startAnimation={inView}
          viewportWidth={dimensions.width}
          viewportHeight={dimensions.height}
        />
      ) : (
        <AnimatedGrid
          gridSize={332}
          startAnimation={inView}
          viewportWidth={dimensions.width}
          viewportHeight={dimensions.height}
        />
      )}
    </svg>
  )
}

const AnimatedGrid = ({
  gridSize,
  startAnimation,
  viewportWidth,
  viewportHeight,
}: {
  gridSize: number
  startAnimation: boolean
  viewportWidth: number
  viewportHeight: number
}) => {
  const rectangles = useMemo(() => {
    if (!viewportWidth || !viewportHeight) return []

    const cols = Math.ceil(viewportWidth / gridSize) + 1 // Add extra column to ensure coverage
    const rows = Math.ceil(viewportHeight / gridSize) + 1 // Add extra row to ensure coverage
    const rects = Array.from({ length: rows })
      .map((_, row) => {
        return Array.from({ length: cols }).map((_, col) => {
          const x = col * gridSize
          const y = row * gridSize
          return {
            id: `rect-${row}-${col}`,
            x,
            y,
            delay: (col + row) / 10,
            path: `M ${x} ${y} L ${x + gridSize} ${y} L ${x + gridSize} ${y + gridSize} L ${x} ${y + gridSize} Z`,
          }
        })
      })
      .flat()
    return rects
  }, [gridSize, viewportWidth, viewportHeight])

  return (
    <g>
      {rectangles.map((rect) => (
        <motion.path
          key={rect.id}
          d={rect.path}
          initial={{
            pathLength: 0,
          }}
          animate={{
            pathLength: startAnimation ? 1 : 0,
          }}
          transition={{
            pathLength: {
              duration: 0.5,
              ease: "linear",
              delay: rect.delay,
            },
          }}
          strokeWidth={1}
          fill="none"
        />
      ))}
    </g>
  )
}
